import {
  analysisSnapshotSchema,
  analysisSnapshotsQuerySchema,
  fxRatesQuerySchema,
  listContractsQuerySchema,
  marketDataPayloadSchema,
  parseDemoDataEnvelope,
  settlementsQuerySchema,
  type AnalysisSnapshot,
  type AnalysisSnapshotsQuery,
  type DailySettlement,
  type DataCoverage,
  type DemoDataEnvelope,
  type FuturesContract,
  type FxRate,
  type FxRatesQuery,
  type ListContractsQuery,
  type SettlementsQuery,
} from "../contracts/schemas";
import type { MarketDataAdapter } from "./market-data-adapter";

export class DemoJsonMarketDataAdapter implements MarketDataAdapter {
  readonly mode = "demo" as const;
  readonly source = "synthetic-fixture" as const;

  private readonly envelope: DemoDataEnvelope;
  private readonly snapshots = new Map<string, AnalysisSnapshot>();

  constructor(value: unknown) {
    this.envelope = parseDemoDataEnvelope(value);

    for (const snapshot of this.envelope.data.analysisSnapshots) {
      this.snapshots.set(snapshot.id, snapshot);
    }
  }

  async listContracts(query: ListContractsQuery = {}): Promise<FuturesContract[]> {
    const parsed = listContractsQuerySchema.parse(query);
    const instrumentIds = parsed.instrumentIds ? new Set(parsed.instrumentIds) : undefined;

    return this.envelope.data.contracts
      .filter((contract) => !instrumentIds || instrumentIds.has(contract.instrumentId))
      .filter((contract) => !parsed.enabledOnly || contract.enabled)
      .filter(
        (contract) =>
          parsed.deliveryYearFrom === undefined ||
          contract.deliveryYear >= parsed.deliveryYearFrom,
      )
      .filter(
        (contract) =>
          parsed.deliveryYearTo === undefined || contract.deliveryYear <= parsed.deliveryYearTo,
      )
      .sort(compareContracts);
  }

  async getSettlements(query: SettlementsQuery): Promise<DailySettlement[]> {
    const parsed = settlementsQuerySchema.parse(query);
    const contractIds = new Set(parsed.contractIds);

    return this.envelope.data.settlements
      .filter((settlement) => contractIds.has(settlement.contractId))
      .filter(
        (settlement) =>
          settlement.exchangeBusinessDate >= parsed.startDate &&
          settlement.exchangeBusinessDate <= parsed.endDate,
      )
      .filter(
        (settlement) =>
          parsed.asOfTimestamp === undefined ||
          isTimestampAtOrBefore(settlement.ingestedAt, parsed.asOfTimestamp),
      )
      .filter(latestRevisionFilter(
        (settlement) => `${settlement.contractId}\u0000${settlement.exchangeBusinessDate}`,
        (settlement) => settlement.ingestedAt,
      ))
      .sort(compareSettlements);
  }

  async getFxRates(query: FxRatesQuery): Promise<FxRate[]> {
    const parsed = fxRatesQuerySchema.parse(query);
    const pairs = new Set(parsed.pairs);

    return this.envelope.data.fxRates
      .filter((rate) => pairs.has(rate.pair))
      .filter((rate) => rate.date >= parsed.startDate && rate.date <= parsed.endDate)
      .filter(
        (rate) =>
          parsed.asOfTimestamp === undefined ||
          isTimestampAtOrBefore(rate.ingestedAt, parsed.asOfTimestamp),
      )
      .filter(latestRevisionFilter(
        (rate) => `${rate.pair}\u0000${rate.date}`,
        (rate) => rate.ingestedAt,
      ))
      .sort(compareFxRates);
  }

  async getCoverage(): Promise<DataCoverage> {
    return this.envelope.coverage;
  }

  async getAnalysisSnapshots(
    query: AnalysisSnapshotsQuery = {},
  ): Promise<AnalysisSnapshot[]> {
    const parsed = analysisSnapshotsQuerySchema.parse(query);
    const pairPresetIds = parsed.pairPresetIds ? new Set(parsed.pairPresetIds) : undefined;

    return [...this.snapshots.values()]
      .filter((snapshot) => !pairPresetIds || pairPresetIds.has(snapshot.pairPresetId))
      .filter(
        (snapshot) => parsed.asOfDate === undefined || snapshot.asOfDate === parsed.asOfDate,
      )
      .filter(
        (snapshot) =>
          parsed.analysisConfigId === undefined ||
          snapshot.analysisConfigId === parsed.analysisConfigId,
      )
      .sort((left, right) => {
        const dateOrder = right.asOfDate.localeCompare(left.asOfDate);
        return dateOrder || left.id.localeCompare(right.id);
      });
  }

  async saveAnalysisSnapshot(snapshot: AnalysisSnapshot): Promise<AnalysisSnapshot> {
    const parsed = analysisSnapshotSchema.parse(snapshot);
    marketDataPayloadSchema.parse({
      ...this.envelope.data,
      analysisSnapshots: [
        ...[...this.snapshots.values()].filter((existing) => existing.id !== parsed.id),
        parsed,
      ],
    });
    this.snapshots.set(parsed.id, parsed);
    return parsed;
  }
}

function compareContracts(left: FuturesContract, right: FuturesContract): number {
  return (
    left.instrumentId.localeCompare(right.instrumentId) ||
    left.deliveryYear - right.deliveryYear ||
    left.deliveryMonth - right.deliveryMonth
  );
}

function compareSettlements(left: DailySettlement, right: DailySettlement): number {
  return (
    left.exchangeBusinessDate.localeCompare(right.exchangeBusinessDate) ||
    left.contractId.localeCompare(right.contractId)
  );
}

function compareFxRates(left: FxRate, right: FxRate): number {
  return left.date.localeCompare(right.date) || left.pair.localeCompare(right.pair);
}

function isTimestampAtOrBefore(value: string, cutoff: string): boolean {
  return Date.parse(value) <= Date.parse(cutoff);
}

function latestRevisionFilter<T>(
  keyOf: (value: T) => string,
  timestampOf: (value: T) => string,
): (value: T, index: number, values: T[]) => boolean {
  return (value, index, values) => {
    const key = keyOf(value);
    const timestamp = Date.parse(timestampOf(value));
    return !values.some(
      (candidate, candidateIndex) =>
        candidateIndex !== index &&
        keyOf(candidate) === key &&
        (Date.parse(timestampOf(candidate)) > timestamp ||
          (Date.parse(timestampOf(candidate)) === timestamp && candidateIndex > index)),
    );
  };
}
